import { Activity } from 'lucide-react';
import { Toaster } from 'sonner';
import * as Tabs from '@radix-ui/react-tabs';
import { useDashboardData } from '@/hooks/useDashboardData';
import { DataHealthCard } from '@/components/dashboard/DataHealthCard';
import { KPITicker } from '@/components/dashboard/KPITicker';
import { EngagementCharts } from '@/components/dashboard/EngagementCharts';
import { QuestionnairePanel } from '@/components/dashboard/QuestionnairePanel';
import { SocialReachPanel } from '@/components/dashboard/SocialReachPanel';
import { ChatAnalyticsPanel } from '@/components/dashboard/ChatAnalyticsPanel';
import { QuizAnalyticsPanel } from '@/components/dashboard/QuizAnalyticsPanel';
import { CacheStatusPanel } from '@/components/dashboard/CacheStatusPanel';
import { ModerationDeck } from '@/components/dashboard/ModerationDeck';
import { SecurityPanel } from '@/components/dashboard/SecurityPanel';
import './App.css';

export default function App() {
  const {
    loading, kpis, engagementData, activityData,
    photos, filterPending, setFilterPending, handleModeration,
    questionnaireData, questionnaireStats,
    socialMetrics, socialTrendData,
    chatAnalytics,
    quizAnalytics, quizFilters, setQuizFilters, showAllTeams, setShowAllTeams,
    availableTeams, availableTiers,
    cacheStatus, cacheMetrics,
    securityData,
    dataHealth
  } = useDashboardData();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-16 h-16 text-[#00ff41] animate-pulse mx-auto mb-4" />
          <p className="text-[#00ff41] text-xl font-semibold">LOADING COMMAND CENTER...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6">
      <Toaster position="top-right" theme="dark" />

      {/* Header */}
      <div className="mb-6" data-testid="dashboard-header">
        <h1 className="text-4xl font-bold text-[#00ff41] tracking-tight" data-testid="dashboard-title">
          fanZecho <span className="text-[#00d1ff]">COMMAND CENTER</span>
        </h1>
        <p className="text-gray-500 mt-2 text-sm">77 entities · 6 sports · Live analytics</p>
      </div>

      {/* Always-visible status strip */}
      <DataHealthCard dataHealth={dataHealth} />

      {/* Tabbed sections */}
      <Tabs.Root defaultValue="ops" className="cmd-tabs">
        <Tabs.List className="cmd-tabs-list" aria-label="Dashboard sections">
          <Tabs.Trigger value="ops" className="cmd-tab-trigger">[OPS]</Tabs.Trigger>
          <Tabs.Trigger value="data" className="cmd-tab-trigger">[DATA]</Tabs.Trigger>
          <Tabs.Trigger value="ext" className="cmd-tab-trigger">[EXT]</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="ops" className="cmd-tab-content">
          <KPITicker kpis={kpis} />
          <CacheStatusPanel cacheStatus={cacheStatus} cacheMetrics={cacheMetrics} />
          <SecurityPanel securityData={securityData} />
        </Tabs.Content>

        <Tabs.Content value="data" className="cmd-tab-content">
          <EngagementCharts engagementData={engagementData} activityData={activityData} />
          <QuizAnalyticsPanel quizAnalytics={quizAnalytics} quizFilters={quizFilters} setQuizFilters={setQuizFilters} showAllTeams={showAllTeams} setShowAllTeams={setShowAllTeams} availableTeams={availableTeams} availableTiers={availableTiers} />
          <QuestionnairePanel questionnaireData={questionnaireData} questionnaireStats={questionnaireStats} />
          <ChatAnalyticsPanel chatAnalytics={chatAnalytics} />
        </Tabs.Content>

        <Tabs.Content value="ext" className="cmd-tab-content">
          <SocialReachPanel socialMetrics={socialMetrics} socialTrendData={socialTrendData} />
          <ModerationDeck photos={photos} filterPending={filterPending} setFilterPending={setFilterPending} handleModeration={handleModeration} />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}
